import React, { useEffect, useState, useRef, useCallback } from "react";
import { Container, Nav, Navbar, NavDropdown } from "react-bootstrap";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import logo from "../../Resources/Transparent_Logo.png";
import Notifications from "../NotesTasks/Notification";
import { SupportModal } from "../Support/SupportModal";
import CreateTaskModal from "../Tasks/CreateTaskModal";
import { userCanUseTasks } from "../Tasks/access";
import { getUserPracticeById, scopePractice } from "../../api";
import { purgeAll as purgeSyncToIDB } from "../SyncTo/hooks/useSyncToIDB";
import { useRecordingActions } from "../SyncTo/RecordingProvider";
import "./BootNav.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { io } from "socket.io-client";

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3002";

// When Azure Web PubSub is configured, clients must connect to the PubSub
// endpoint (not the backend directly). The backend registers as the hub server
// and Azure routes messages between clients and server.
const SOCKET_URL = process.env.REACT_APP_PUBSUB_PROD_URL || process.env.REACT_APP_PUBSUB_URL || URL;
const SOCKET_PATH = (process.env.REACT_APP_PUBSUB_PROD_URL || process.env.REACT_APP_PUBSUB_URL)
  ? `/clients/socketio/hubs/${process.env.REACT_APP_PUBSUB_HUB || "Hub"}`
  : "/socket.io";

/**
 * mergeNotifications(prev, incoming)
 *
 * Merges the authoritative DB list (incoming) with the current state (prev).
 * - Incoming DB records win over any existing copy with the same _id.
 * - Synthetic entries from prev (those with no _id, e.g. statement-run-progress)
 *   are always preserved — the API never returns them.
 * - Result is sorted by timestamp descending.
 */
function mergeNotifications(prev, incoming) {
  // Synthetic entries from prev: those with no _id (e.g. statement-run progress
  // or in-flight syncto rows pushed before the backend persisted its copy).
  // We drop synthetic entries whose dedupeKey matches an incoming DB record so
  // the bell shows ONE row per job (not the synthetic + persisted pair).
  const incomingDedupeKeys = new Set(
    incoming.map((n) => n.dedupeKey).filter(Boolean)
  );
  const incomingIds = new Set(
    incoming.map((n) => String(n._id || n.id)).filter(Boolean)
  );
  const syntheticFromPrev = prev.filter(
    (n) => !n._id
      && (!n.dedupeKey || !incomingDedupeKeys.has(n.dedupeKey))
      && !incomingIds.has(String(n.id))
  );
  return [
    ...incoming.map((n) => ({ ...n, id: n._id || n.id })),
    ...syntheticFromPrev,
  ].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
}

function NavBar({
  notifications,
  addNotification,
  onRemoveNotification,
  onMarkAsRead,
  setNotifications,
}) {
  const [expanded, setExpanded] = useState(false);
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [userRole, setUserRole] = useState(false); // SuperAdmin
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [scopedPractice, setScopedPractice] = useState(null);
  const [userPractices, setUserPractices] = useState([]);
  const [canUseMultiPrac, setCanUseMultiPrac] = useState(false);
  // JWT-derived gate. Recomputed when state changes; cheap (sessionStorage
  // read + decode), no need to memoise unless this re-renders a lot.
  const canUseTasks = userCanUseTasks();
  const socketRef = useRef(null);
  const userIdRef = useRef(null);
  const notificationsLoadedRef = useRef(false);

  // Stage 3 — SyncTo background-job hook. RecordingProvider lives above this
  // component in the App tree (NotificationContext > RecordingProvider >
  // HashRouter > BootNav), so useRecording() is in scope. The 'syncto:job-
  // complete' socket listener calls openJobForReview to surface ReviewModal.
  const recording = useRecordingActions();
  const recordingRef = useRef(null);
  useEffect(() => { recordingRef.current = recording; }, [recording]);

  const stableAddNotification = useCallback(
    (notification) => {
      if (typeof addNotification === "function") addNotification(notification);
    },
    [addNotification]
  );

  const loadNotifications = useCallback(
    async (userId, { force = false } = {}) => {
      if (!force && notificationsLoadedRef.current && userIdRef.current === userId)
        return;
      try {
        const response = await axios.get(`${URL}/notifications`);
        const dbNotifications = response.data.map((n) => ({ ...n, id: n._id }));
        if (force) {
          setNotifications((prev) => mergeNotifications(prev, dbNotifications));
        } else {
          setNotifications(dbNotifications);
        }
        notificationsLoadedRef.current = true;
        userIdRef.current = userId;
      } catch (e) {
        console.error("Failed to load notifications:", e);
      }
    },
    [setNotifications]
  );

  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (!token) {
      notificationsLoadedRef.current = false;
      userIdRef.current = null;
      return;
    }

    let decoded;
    try {
      decoded = jwtDecode(token);
      setUserFirstName(decoded.firstName || "");
      setUserLastName(decoded.lastName || "");

      const rolesFromRole = Array.isArray(decoded?.role)
        ? decoded.role
        : decoded?.role
        ? [decoded.role]
        : [];
      const rolesFromPermissions = Array.isArray(decoded?.permissions)
        ? decoded.permissions
        : [];
      const roles = [...new Set([...rolesFromRole, ...rolesFromPermissions])];
      setUserRole(roles.some((r) => ["SuperAdmin"].includes(r)));
      setIsSystemAdmin(roles.some((r) => r === "SystemAdmin"));
      setScopedPractice(decoded._scopedPractice || null);
      setCanUseMultiPrac(!!decoded?.features?.multiPrac);

      axios.defaults.headers.common["authorization"] = `Bearer ${token}`;
    } catch (e) {
      console.error("Failed to decode token:", e);
      return;
    }

    if (!decoded._id) return;

    loadNotifications(decoded._id);

    if (!socketRef.current || userIdRef.current !== decoded._id) {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
      }

      const socket = io(SOCKET_URL, {
        path: SOCKET_PATH,
        auth: { token },                    // JWT for Phase 26 handshake middleware
        query: { userId: decoded._id },     // local-dev bypass fallback (server reads query.userId when WEB_PUBSUB_CONNECTION_STRING is absent)
        transports: ["websocket", "polling"],
        forceNew: true,
      });

      socketRef.current = socket;

      // Fires on initial connect AND every successful reconnect after disconnect
      socket.on("connect", () => {
        loadNotifications(decoded._id, { force: true });
      });

      socket.on("pdf-generation-complete", (data) =>
        stableAddNotification({
          message: data.message,
          downloadId: data.downloadId,
          type: "pdf",
          timestamp: Date.now(),
          id: data.id || Date.now() + Math.random(),
        })
      );

      socket.on("appointment-confirmation-email-sent", (data) =>
        stableAddNotification({
          message: data.message,
          timestamp: data.timestamp,
          type: "email",
          id: data.id || Date.now() + Math.random(),
        })
      );

      socket.on("statement-email-sent", (data) => {
        console.log("Statement email notification received:", data);
        stableAddNotification({
          message: data.message,
          timestamp: data.timestamp,
          type: "email",
          id: data.id || `${Date.now()}-${Math.random()}`,
        });
      });

      socket.on("invoice-email-sent", (data) =>
        stableAddNotification({
          message: data.message,
          timestamp: data.timestamp,
          type: "email",
          id: data.id || `${Date.now()}-${Math.random()}`,
        })
      );

      socket.on("receipt-email-sent", (data) =>
        stableAddNotification({
          message: data.message,
          timestamp: data.timestamp,
          type: "email",
          id: data.id || `${Date.now()}-${Math.random()}`,
        })
      );

      socket.on("creditnote-email-sent", (data) =>
        stableAddNotification({
          message: data.message,
          timestamp: data.timestamp,
          type: "email",
          id: data.id || `${Date.now()}-${Math.random()}`,
        })
      );

      socket.on("era-received", (data) =>
        stableAddNotification({
          message: data.message,
          timestamp: data.timestamp,
          type: "era",
          id: data.id || `${Date.now()}-${Math.random()}`,
        })
      );

      // SyncTo transcription job complete. Worker pushes this with payload
      // { jobId, sessionId, patientId, mode, message, … }. We UPDATE the
      // existing in-flight "Processing…" notification in place (statement-
      // worker pattern), and open the ReviewModal ONLY when the user is
      // currently on the matching patient's page — otherwise the bell update
      // is the cue and they click through to land + auto-open via
      // pollPendingForPatient on patient mount.
      socket.on("syncto:job-complete", (data) => {
        if (!data || !data.jobId) return;
        setNotifications((prev) => {
          const idx = prev.findIndex(
            (n) => n.type === "syncto" && (n.meta?.jobId === data.jobId || n.jobId === data.jobId || n.id === `syncto-job-${data.jobId}`)
          );
          const next = {
            id: `syncto-job-${data.jobId}`,
            dedupeKey: `syncto-job-${data.jobId}`,
            message: data.message || "Your transcription is ready for review",
            timestamp: data.timestamp || Date.now(),
            type: "syncto",
            isRead: false,
            jobId: data.jobId,
            sessionId: data.sessionId,
            patientId: data.patientId,
            mode: data.mode,
            meta: {
              jobId: data.jobId,
              sessionId: data.sessionId,
              patientId: data.patientId,
              mode: data.mode,
              status: "COMPLETE",
            },
          };
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = { ...prev[idx], ...next };
            return updated;
          }
          return [next, ...prev];
        });
        const rec = recordingRef.current;
        const patientId = data.patientId ? String(data.patientId).toLowerCase() : '';
        const onPatientPage = patientId && (window.location.hash || '').toLowerCase().includes(`/patients/appointment/${patientId}`);
        if (onPatientPage && rec && typeof rec.openJobForReview === "function") {
          rec.openJobForReview(data.jobId);
        }
      });

      // SyncTo transcription job failed — same in-place update pattern.
      socket.on("syncto:job-failed", (data) => {
        if (!data) return;
        setNotifications((prev) => {
          const idx = prev.findIndex(
            (n) => n.type === "syncto" && (n.meta?.jobId === data.jobId || n.jobId === data.jobId || n.id === `syncto-job-${data.jobId}`)
          );
          const next = {
            id: data.jobId ? `syncto-job-${data.jobId}` : `syncto-failed-${Date.now()}`,
            dedupeKey: data.jobId ? `syncto-job-${data.jobId}` : undefined,
            message: data.message || "Transcription failed — please try again.",
            timestamp: data.timestamp || Date.now(),
            type: "syncto",
            isRead: false,
            jobId: data.jobId,
            meta: { jobId: data.jobId, status: "FAILED" },
          };
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = { ...prev[idx], ...next };
            return updated;
          }
          return [next, ...prev];
        });
      });

      // Dashboard Tasks — @mention notification. Persisted server-side via
      // notifyUser; this socket listener surfaces the live bell badge update
      // so the user sees the count tick up without a page refresh. Click
      // handler in Notification.jsx navigates to /home?taskId=… and opens
      // the TaskDetailModal.
      socket.on("task:mentioned", (data) => {
        if (!data) return;
        stableAddNotification({
          message:   data.message || "You were mentioned in a task.",
          timestamp: data.timestamp || Date.now(),
          type:      "task",
          id:        data.id || (data.commentId ? `task-mention-${data.taskId}-${data.commentId}` : `task-${data.taskId || Date.now()}`),
          taskId:    data.taskId,
          commentId: data.commentId,
          dedupeKey: data.dedupeKey,
        });
      });

      // Dashboard Tasks — AI-suggested follow-up assignment. The
      // transcription-review task is already covered by 'syncto:job-complete'
      // so the worker only fires this event for AI_FOLLOW_UP rows.
      socket.on("task:assigned", (data) => {
        if (!data) return;
        stableAddNotification({
          message:   data.message || "A new task has been assigned to you.",
          timestamp: data.timestamp || Date.now(),
          type:      "task",
          id:        data.id || (data.taskId ? `task-assigned-${data.taskId}` : `task-${Date.now()}`),
          taskId:    data.taskId,
          dedupeKey: data.dedupeKey,
        });
      });

      socket.on("statement-run-progress", (data) => {
        const processed = data.processed || 0;
        const total = data.total || 0;
        // Update or add a single notification per run showing progress
        setNotifications((prev) => {
          const existingIdx = prev.findIndex(
            (n) => n.type === "statement-run" && n.meta?.runId === data.runId
          );
          const notification = {
            id: `sr-${data.runId}`,
            message: `Bulk statements: ${processed}/${total}`,
            timestamp: data.timestamp || Date.now(),
            type: "statement-run",
            isRead: false,
            meta: {
              runId: data.runId,
              processed,
              total,
              status: data.status || "RUNNING",
            },
          };
          if (existingIdx >= 0) {
            const updated = [...prev];
            updated[existingIdx] = notification;
            return updated;
          }
          return [notification, ...prev];
        });
      });

      socket.on("statement-run-complete", (data) => {
        const processed = data.processed || 0;
        const total = data.total || 0;
        const statusLabel = data.status?.replace(/_/g, " ").toLowerCase() || "completed";
        setNotifications((prev) => {
          const existingIdx = prev.findIndex(
            (n) => n.type === "statement-run" && n.meta?.runId === data.runId
          );
          const notification = {
            id: `sr-${data.runId}`,
            message: `Bulk statements ${statusLabel}: ${processed}/${total}`,
            timestamp: Date.now(),
            type: "statement-run",
            isRead: false,
            meta: {
              runId: data.runId,
              processed,
              total,
              status: data.status || "COMPLETED",
            },
          };
          if (existingIdx >= 0) {
            const updated = [...prev];
            updated[existingIdx] = notification;
            return updated;
          }
          return [notification, ...prev];
        });
      });

      socket.on("support-request-created", (data) =>
        stableAddNotification({
          message: data.message || "Support request submitted successfully",
          timestamp: data.timestamp || Date.now(),
          type: "support",
          id: data.id || `${Date.now()}-${Math.random()}`,
        })
      );
    }

    return () => {
      if (socketRef.current && userIdRef.current !== decoded._id) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [loadNotifications, stableAddNotification]);

  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        notificationsLoadedRef.current = false;
        userIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (!token) return;
    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch {
      return;
    }
    if (!decoded?._id) return;
    // Only fetch the user's true practice list if the multiPrac feature is on
    // — otherwise we'd never render the switcher and the fetch is wasted.
    if (!decoded?.features?.multiPrac) return;
    // includeAll: true — the switcher needs the user's full true practice
    // list (so a scoped user can switch back to "All"). Honoured by the
    // backend only for self.
    getUserPracticeById(decoded._id, { includeAll: true }).then((practices) => {
      if (Array.isArray(practices)) setUserPractices(practices);
    });
  }, []);

  const handleScopeChange = async (practiceNumber) => {
    const res = await scopePractice(practiceNumber);
    if (res.success) {
      window.location.hash = "#/home";
      window.location.reload();
    } else {
      alert(
        "Could not change practice scope: " + (res.error || "unknown error")
      );
    }
  };

  const handleLogout = () => {
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    notificationsLoadedRef.current = false;
    userIdRef.current = null;
    // Phase 30 — PITFALLS #6 site B: purge SyncTo IndexedDB rows before the
    // session clears so recordings cannot leak across users on a shared
    // workstation. Fire-and-forget; handleLogout is synchronous and must stay
    // so (upstream callers rely on immediate state teardown).
    try { purgeSyncToIDB().catch(() => {}); } catch {}
    sessionStorage.removeItem("user");
    setExpanded(false);
  };

  const closeMenu = () => setExpanded(false);

  const isActive = (hash) => window.location.hash.startsWith(hash);

  return (
    <Navbar
      expand="lg"
      className="fixed-top bg-white brand-topbar"
      expanded={expanded}
      onToggle={(isExpanded) => setExpanded(isExpanded)}
    >
      <Container fluid className="px-lg-3">
        {/* Brand (left) */}
        <Navbar.Brand href="#/home" className="brand ms-4 order-0">
          <img className="logo" src={logo} alt="Logo" />
        </Navbar.Brand>

        {/* Right actions (desktop only) — we keep this OUTSIDE collapse and push to far right.
            All triggers share p-2 + inline-flex centering so the icon baseline lines up with the
            nav-rail pills and the user/practice dropdown labels on a single horizontal centerline. */}
        <div className="topbar-actions ms-auto d-none d-lg-flex align-items-center order-lg-2">
          {canUseTasks && (
            <button
              type="button"
              className="btn btn-link p-2 text-decoration-none d-inline-flex align-items-center"
              title="New task"
              aria-label="New task"
              onClick={() => setShowCreateTaskModal(true)}
              style={{ border: 'none', background: 'none' }}
            >
              <i className="fa-light fa-clipboard-list-check fs-6 text-primary" />
            </button>
          )}
          <button
            type="button"
            className="btn btn-link p-2 text-decoration-none d-inline-flex align-items-center"
            title="Support"
            aria-label="Support"
            onClick={() => setShowSupportModal(true)}
            style={{ border: 'none', background: 'none' }}
          >
            <i className="fa-light fa-life-ring fs-6 text-primary" />
          </button>
          <Notifications
            notifications={notifications}
            onRemoveNotification={onRemoveNotification}
            onMarkAsRead={onMarkAsRead}
          />

          <NavDropdown
            className="user-dropdown ms-2 rounded-0"
            align="end"
            id="user-nav-dropdown"
            title={
              <span className="user-trigger d-inline-flex align-items-center">
                <i className="fa-light fa-circle-user me-2 fs-6 text-primary" />
                <span className="name">
                  {userFirstName} {userLastName}
                </span>
              </span>
            }
          >
            <NavDropdown.Item href="#/userprofile" onClick={closeMenu}>
              Your Profile
            </NavDropdown.Item>
            <NavDropdown.Divider />
            <NavDropdown.Item href="#/" onClick={handleLogout}>
              Log Out
            </NavDropdown.Item>
          </NavDropdown>

          {/* Active-practice switcher — to the right of the username (desktop only) */}
          {canUseMultiPrac && userPractices.length > 1 && (
            <NavDropdown
              className="practice-scope-dropdown ms-2"
              align="end"
              id="practice-scope-dropdown"
              title={
                <span className="d-inline-flex align-items-center">
                  <i className="fa-light fa-house-medical me-2 fs-6 text-primary" />
                  {scopedPractice
                    ? userPractices.find(
                        (p) => p.practiceNumber === scopedPractice
                      )?.practiceName || `Practice ${scopedPractice}`
                    : `All practices (${userPractices.length})`}
                </span>
              }
            >
              <NavDropdown.Item
                active={!scopedPractice}
                onClick={() => handleScopeChange(null)}
              >
                All practices ({userPractices.length})
              </NavDropdown.Item>
              <NavDropdown.Divider />
              {userPractices.map((p) => (
                <NavDropdown.Item
                  key={p.practiceNumber}
                  active={scopedPractice === p.practiceNumber}
                  onClick={() => handleScopeChange(p.practiceNumber)}
                >
                  {p.practiceName}
                </NavDropdown.Item>
              ))}
            </NavDropdown>
          )}
        </div>

        {/* Toggler (always visible) */}
        <Navbar.Toggle
          aria-controls="basic-navbar-nav"
          className="border-0 order-1 ms-auto d-lg-none"
        />

        {/* Collapsed content (mobile) & middle rail (desktop) */}
        <Navbar.Collapse id="basic-navbar-nav" className="order-3 order-lg-1">
          {/* Primary rail (desktop) */}
          <Nav className="nav-rail mx-lg-2 d-none d-lg-flex">
            <Nav.Link
              className={`nav-pill ${isActive("#/home") ? "active" : ""}`}
              href="#/home"
              onClick={closeMenu}
            >
              Dashboard
            </Nav.Link>

            <Nav.Link
              className={`nav-pill ${isActive("#/bookings") ? "active" : ""}`}
              href="#/bookings"
              onClick={closeMenu}
            >
              Appointments
            </Nav.Link>

            <Nav.Link
              className={`nav-pill ${isActive("#/billing") ? "active" : ""}`}
              href="#/billing"
              onClick={closeMenu}
            >
              Accounts
            </Nav.Link>

            {userRole && (
              <Nav.Link
                className={`nav-pill ${isActive("#/practice") ? "active" : ""}`}
                href="#/practice"
                onClick={closeMenu}
              >
                Practice Settings
              </Nav.Link>
            )}
            {isSystemAdmin && (
              <Nav.Link
                className={`nav-pill ${isActive("#/datamigration") ? "active" : ""}`}
                href="#/datamigration"
                onClick={closeMenu}
              >
                Data Migration
              </Nav.Link>
            )}
          </Nav>

          {/* Mobile-only menu (limited items) */}
          <Nav className="d-lg-none px-2 pt-2 border-top mt-2">
            <Nav.Link href="#/home" onClick={closeMenu}>
              Dashboard
            </Nav.Link>
            <Nav.Link href="#/bookings" onClick={closeMenu}>
              Appointments
            </Nav.Link>
            {canUseTasks && (
              <Nav.Link
                onClick={() => {
                  closeMenu();
                  setShowCreateTaskModal(true);
                }}
                style={{ cursor: "pointer" }}
              >
                <i className="fa-light fa-clipboard-list-check me-2" />
                New Task
              </Nav.Link>
            )}
            <Nav.Link
              onClick={() => {
                closeMenu();
                setShowSupportModal(true);
              }}
              style={{ cursor: "pointer" }}
            >
              <i className="fa-light fa-life-ring me-2" />
              Support
            </Nav.Link>
            <Nav.Link href="#/userprofile" onClick={closeMenu}>
              Your Profile
            </Nav.Link>
            <Nav.Link href="#/" onClick={handleLogout}>
              Log Out
            </Nav.Link>
          </Nav>
        </Navbar.Collapse>

        {/* Mobile floating notifications (bottom-right) */}
        <div className="mobile-fab d-lg-none rounded-circle">
          <Notifications
            notifications={notifications}
            onRemoveNotification={onRemoveNotification}
            onMarkAsRead={onMarkAsRead}
          />
        </div>
      </Container>

      <SupportModal
        show={showSupportModal}
        onHide={() => setShowSupportModal(false)}
      />

      {canUseTasks && (
        <CreateTaskModal
          show={showCreateTaskModal}
          onClose={() => setShowCreateTaskModal(false)}
          onCreated={() => {
            // Nudge the dashboard's TasksSection to refresh immediately
            // rather than waiting for the 30s poll. TasksSection listens
            // for this event on window and calls its refresh() handler.
            window.dispatchEvent(new CustomEvent('tasks:created'));
          }}
        />
      )}

    </Navbar>
  );
}

export default NavBar;
